import React, { useMemo, useState } from "react";
import Papa from "papaparse";
import JSZip from "jszip";

/* ============================================================================
   NASCAR Contest Dashboard
   - Upload DraftKings contest standings CSV or ZIP
   - Contest overview
   - Field exposure
   - User exposure
   - User portfolio view
   - User leverage vs field
   - Lineup duplicates
   - Most duped lineups
   - Sortable/filterable table headers
============================================================================ */

function lower(v) {
  return String(v ?? "").toLowerCase();
}

function toNum(v) {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function fmtNum(v, digits = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function fmtPct(v, digits = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.0%";
  return `${n.toFixed(digits)}%`;
}

function formatLeverage(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.0%";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

function cleanName(v) {
  return String(v ?? "").trim();
}

function cleanEntryName(name = "") {
  return String(name)
    .replace(/\s*\(\d+\/\d+\)\s*/g, "")
    .trim();
}

function normalizeDriverName(name = "") {
  return String(name)
    .replace(/\s+/g, " ")
    .replace(/\s+Jr\.$/i, " Jr")
    .trim();
}

function normalizeLineupKey(players = []) {
  return players
    .map(normalizeDriverName)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(" | ");
}

function detectColumn(columns, candidates) {
  const cleanMap = new Map(
    columns.map((c) => [lower(String(c).trim()).replace(/\s+/g, ""), c])
  );

  for (const cand of candidates) {
    const key = lower(cand).replace(/\s+/g, "");
    if (cleanMap.has(key)) return cleanMap.get(key);
  }

  for (const cand of candidates) {
    const hit = columns.find((c) =>
      lower(c).replace(/\s+/g, "").includes(lower(cand).replace(/\s+/g, ""))
    );
    if (hit) return hit;
  }

  return "";
}

function parseLineupPlayers(lineup = "") {
  const raw = String(lineup ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return [];

  const positionTokens =
    "CPT|UTIL|FLEX|D|P|QB|RB|WR|TE|DST|C|PG|SG|SF|PF|OF|IF|G|F";

  const players = raw
    .split(new RegExp(`\\s+(?=(?:${positionTokens})\\s)`, "g"))
    .map((part) =>
      part
        .replace(new RegExp(`^(?:${positionTokens})\\s+`, "i"), "")
        .trim()
    )
    .map(normalizeDriverName)
    .filter(Boolean);

  // A valid DFS lineup should never contain the same player twice.
  // De-duping here prevents a malformed row from inflating field exposure.
  return [...new Set(players)];
}

function stripBom(text = "") {
  return String(text).replace(/^\uFEFF/, "");
}

function isZipFile(file) {
  const name = lower(file?.name);
  return (
    name.endsWith(".zip") ||
    file?.type === "application/zip" ||
    file?.type === "application/x-zip-compressed"
  );
}

async function readContestFile(file) {
  if (!isZipFile(file)) {
    return {
      csvText: stripBom(await file.text()),
      sourceLabel: file.name,
      extractedFileName: file.name,
    };
  }

  const zip = await JSZip.loadAsync(file);

  const csvFiles = Object.values(zip.files).filter((entry) => {
    const name = lower(entry.name);
    return (
      !entry.dir &&
      name.endsWith(".csv") &&
      !name.startsWith("__macosx/") &&
      !name.split("/").pop()?.startsWith(".")
    );
  });

  if (!csvFiles.length) {
    throw new Error("No CSV file was found inside the ZIP.");
  }

  const preferred =
    csvFiles.find((entry) => lower(entry.name).includes("contest-standings")) ||
    csvFiles.find((entry) => lower(entry.name).includes("standings")) ||
    csvFiles[0];

  const csvText = stripBom(await preferred.async("string"));

  return {
    csvText,
    sourceLabel: `${file.name} → ${preferred.name}`,
    extractedFileName: preferred.name,
  };
}

function csvDownload(filename, rows) {
  const csv = Papa.unparse(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}

function sortRows(rows, sort) {
  if (!sort?.key) return rows;

  const dir = sort.dir === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const av = a?.[sort.key];
    const bv = b?.[sort.key];

    const an =
      typeof av === "number"
        ? av
        : Number(String(av ?? "").replace(/[%,$]/g, ""));
    const bn =
      typeof bv === "number"
        ? bv
        : Number(String(bv ?? "").replace(/[%,$]/g, ""));

    if (Number.isFinite(an) && Number.isFinite(bn)) {
      return (an - bn) * dir;
    }

    return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
  });
}

function getFilterValue(row, key) {
  const v = row?.[key];
  if (Array.isArray(v)) return v.join(", ");
  return v;
}

function applyColumnFilters(rows, filters) {
  const active = Object.entries(filters || {}).filter(([, v]) =>
    String(v ?? "").trim()
  );

  if (!active.length) return rows;

  return rows.filter((row) =>
    active.every(([key, val]) =>
      lower(getFilterValue(row, key)).includes(lower(String(val).trim()))
    )
  );
}

/* ============================================================================
   Main Page
============================================================================ */

export default function NascarContestDashboardPage() {
  const [rawRows, setRawRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const [globalSearch, setGlobalSearch] = useState("");
  const [sorts, setSorts] = useState({});
  const [filters, setFilters] = useState({});
  const [selectedUser, setSelectedUser] = useState("");

  function setTableSort(tableKey, colKey) {
    setSorts((prev) => {
      const current = prev[tableKey] || { key: "", dir: "asc" };
      const next =
        current.key === colKey
          ? { key: colKey, dir: current.dir === "asc" ? "desc" : "asc" }
          : { key: colKey, dir: "asc" };

      return { ...prev, [tableKey]: next };
    });
  }

  function setTableFilter(tableKey, colKey, value) {
    setFilters((prev) => ({
      ...prev,
      [tableKey]: {
        ...(prev[tableKey] || {}),
        [colKey]: value,
      },
    }));
  }

  function clearFilters(tableKey) {
    setFilters((prev) => ({
      ...prev,
      [tableKey]: {},
    }));
  }

  async function processContestFile(file) {
    if (!file) return;

    setFileName(file.name);
    setParseError("");
    setRawRows([]);
    setSelectedUser("");
    setGlobalSearch("");
    setFilters({});
    setSorts({});

    try {
      const { csvText, sourceLabel } = await readContestFile(file);

      if (!csvText.trim()) {
        throw new Error("The selected file is empty.");
      }

      const results = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (h) => stripBom(String(h ?? "")).trim(),
      });

      const fatalError = (results.errors || []).find(
        (err) => err?.type === "Quotes" || err?.type === "Delimiter"
      );

      if (fatalError) {
        throw new Error(fatalError.message || "The CSV could not be parsed.");
      }

      const rows = Array.isArray(results.data) ? results.data : [];
      if (!rows.length) {
        throw new Error("The CSV did not contain any rows.");
      }

      const columns = Object.keys(rows?.[0] || {});
      const entryCol = detectColumn(columns, ["EntryName", "Entry Name"]);
      const entryIdCol = detectColumn(columns, ["EntryId", "Entry ID"]);
      const rankCol = detectColumn(columns, ["Rank"]);
      const pointsCol = detectColumn(columns, ["Points", "Fantasy Points"]);
      const lineupCol = detectColumn(columns, ["Lineup"]);
      const rosterPositionCol = detectColumn(columns, ["Roster Position"]);
      const playerCol = detectColumn(columns, ["Player"]);
      const draftedCol = detectColumn(columns, [
        "%Drafted",
        "Drafted",
        "Ownership",
      ]);
      const fptsCol = detectColumn(columns, ["FPTS"]);

      if (!lineupCol || !entryCol) {
        throw new Error(
          "Could not find the required DraftKings columns. The contest file needs EntryName and Lineup columns."
        );
      }

      const parsed = rows
        .map((r, idx) => {
          const players = parseLineupPlayers(r?.[lineupCol]);
          const lineupKey = normalizeLineupKey(players);

          return {
            rowId: `${r?.[entryIdCol] || idx}-${idx}`,
            rank: r?.[rankCol] || "",
            entryId: r?.[entryIdCol] || "",
            entryName: cleanName(r?.[entryCol]),
            username: cleanEntryName(r?.[entryCol]),
            points: toNum(r?.[pointsCol]),
            lineupRaw: r?.[lineupCol] || "",
            players,
            lineupKey,

            // DraftKings includes its official player ownership table in the
            // same standings CSV. Preserve those values for cross-checking.
            dkPlayer: normalizeDriverName(r?.[playerCol] || ""),
            dkRosterPosition: r?.[rosterPositionCol] || "",
            dkDrafted: r?.[draftedCol] || "",
            dkFpts: r?.[fptsCol] || "",
          };
        })
        .filter((r) => r.username && r.players.length);

      if (!parsed.length) {
        throw new Error("No valid contest lineups were found in this file.");
      }

      setFileName(sourceLabel);
      setRawRows(parsed);
      setActiveTab("overview");
    } catch (err) {
      console.error("Contest upload failed:", err);
      setRawRows([]);
      setParseError(`Could not read this file: ${String(err?.message || err)}`);
    }
  }

  function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (file) processContestFile(file);

    // Allows selecting the same file again after clearing or fixing an error.
    e.target.value = "";
  }


  const contest = useMemo(() => {
    const totalLineups = rawRows.length;

    const userMap = new Map();
    const driverMap = new Map();
    const lineupMap = new Map();
    const dkOwnershipMap = new Map();

    // DraftKings places the player ownership table alongside the standings
    // rows. Capture each player once so calculated exposure can be validated.
    for (const row of rawRows) {
      if (!row.dkPlayer) continue;

      const drafted = toNum(row.dkDrafted);
      if (!dkOwnershipMap.has(row.dkPlayer)) {
        dkOwnershipMap.set(row.dkPlayer, {
          driver: row.dkPlayer,
          officialExposure: drafted,
          fpts: toNum(row.dkFpts),
          rosterPosition: row.dkRosterPosition || "",
        });
      }
    }

    for (const row of rawRows) {
      if (!userMap.has(row.username)) {
        userMap.set(row.username, {
          username: row.username,
          rosters: 0,
          lineupKeys: new Set(),
          driverCounts: new Map(),
          entries: [],
          pointsTotal: 0,
          bestRank: null,
          bestPoints: null,
        });
      }

      const user = userMap.get(row.username);
      user.rosters += 1;
      user.lineupKeys.add(row.lineupKey);
      user.entries.push(row);
      user.pointsTotal += toNum(row.points);

      const rankNum = toNum(row.rank);
      if (rankNum && (user.bestRank == null || rankNum < user.bestRank)) {
        user.bestRank = rankNum;
      }

      if (user.bestPoints == null || row.points > user.bestPoints) {
        user.bestPoints = row.points;
      }

      for (const driver of row.players) {
        driverMap.set(driver, (driverMap.get(driver) || 0) + 1);
        user.driverCounts.set(driver, (user.driverCounts.get(driver) || 0) + 1);
      }

      if (!lineupMap.has(row.lineupKey)) {
        lineupMap.set(row.lineupKey, {
          lineupKey: row.lineupKey,
          players: row.players,
          count: 0,
          users: new Set(),
          entries: [],
          pointsTotal: 0,
          bestRank: null,
          bestPoints: null,
        });
      }

      const lineup = lineupMap.get(row.lineupKey);
      lineup.count += 1;
      lineup.users.add(row.username);
      lineup.entries.push(row);
      lineup.pointsTotal += toNum(row.points);

      const rankNum2 = toNum(row.rank);
      if (rankNum2 && (lineup.bestRank == null || rankNum2 < lineup.bestRank)) {
        lineup.bestRank = rankNum2;
      }

      if (lineup.bestPoints == null || row.points > lineup.bestPoints) {
        lineup.bestPoints = row.points;
      }
    }

    const fieldExposure = Array.from(driverMap.entries())
      .map(([driver, count]) => {
        const exposure = totalLineups ? (count / totalLineups) * 100 : 0;
        const official = dkOwnershipMap.get(driver);
        const officialExposure = official?.officialExposure ?? null;

        return {
          driver,
          lineups: count,
          exposure,
          officialExposure,
          exposureDiff:
            officialExposure == null ? null : exposure - officialExposure,
          dkFpts: official?.fpts ?? null,
        };
      })
      .sort((a, b) => b.exposure - a.exposure);

    const fieldExposureByDriver = new Map(
      fieldExposure.map((d) => [d.driver, d.exposure])
    );

    const users = Array.from(userMap.values())
      .map((u) => {
        const driverExposures = Array.from(u.driverCounts.entries())
          .map(([driver, count]) => {
            const exposure = u.rosters ? (count / u.rosters) * 100 : 0;
            const fieldExposure = fieldExposureByDriver.get(driver) || 0;
            const leverage = exposure - fieldExposure;

            return {
              driver,
              lineups: count,
              exposure,
              fieldExposure,
              leverage,
            };
          })
          .sort((a, b) => b.exposure - a.exposure);

        const fieldUniqueEntries = u.entries.filter((entry) => {
          const lineup = lineupMap.get(entry.lineupKey);
          return lineup?.count === 1;
        }).length;

        const fieldDupedEntries = u.entries.filter((entry) => {
          const lineup = lineupMap.get(entry.lineupKey);
          return lineup?.count > 1;
        }).length;

        const personalDuplicateEntries = u.rosters - u.lineupKeys.size;

        const lineupRows = u.entries
          .map((entry) => {
            const lineup = lineupMap.get(entry.lineupKey);
            return {
              entryId: entry.entryId,
              rank: entry.rank,
              points: entry.points,
              lineupKey: entry.lineupKey,
              lineup: entry.players.join(" / "),
              players: entry.players,
              fieldDupeCount: lineup?.count || 1,
              isSolo: (lineup?.count || 1) === 1,
            };
          })
          .sort((a, b) => toNum(a.rank) - toNum(b.rank));

        return {
          username: u.username,
          rosters: u.rosters,

          // How many different lineups this user personally entered.
          builds: u.lineupKeys.size,

          // How many of their entries are unique across the full contest.
          fieldUniqueEntries,

          // How many of their entries are duplicated by at least one other contest entry.
          fieldDupedEntries,

          // How many times they personally repeated the same exact lineup.
          personalDuplicateEntries,
          uniqueRate: u.rosters ? (fieldUniqueEntries / u.rosters) * 100 : 0,

          playersUsed: u.driverCounts.size,
          maxExposure: driverExposures[0]?.exposure || 0,
          topDriver: driverExposures[0]?.driver || "",
          avgPoints: u.rosters ? u.pointsTotal / u.rosters : 0,
          bestPoints: u.bestPoints ?? 0,
          bestRank: u.bestRank ?? "",
          driverExposures,
          entries: u.entries,
          lineupRows,
        };
      })
      .sort((a, b) => b.rosters - a.rosters);

    const lineups = Array.from(lineupMap.values())
      .map((l) => ({
        lineupKey: l.lineupKey,
        lineup: l.players.join(" / "),
        players: l.players,
        dupes: l.count,
        uniqueUsers: l.users.size,
        users: Array.from(l.users),
        avgPoints: l.count ? l.pointsTotal / l.count : 0,
        bestPoints: l.bestPoints ?? 0,
        bestRank: l.bestRank ?? "",
        entries: l.entries,
      }))
      .sort((a, b) => b.dupes - a.dupes || b.avgPoints - a.avgPoints);

    const duplicatedLineups = lineups.filter((l) => l.dupes > 1);
    const mostDuped = duplicatedLineups[0] || null;

    const entriesInDuplicatedLineups = duplicatedLineups.reduce(
      (sum, l) => sum + l.dupes,
      0
    );

    // Number of copies beyond the first instance of each lineup.
    // Example: a lineup appearing 4x contributes 3 extra duplicates.
    const extraDuplicateEntries = duplicatedLineups.reduce(
      (sum, l) => sum + Math.max(0, l.dupes - 1),
      0
    );

    const soloLineups = lineups.filter((l) => l.dupes === 1).length;
    const soloRate = totalLineups ? (soloLineups / totalLineups) * 100 : 0;

    return {
      totalLineups,
      totalUsers: users.length,
      uniqueLineups: lineups.length,
      duplicatedCombos: duplicatedLineups.length,
      entriesInDuplicatedLineups,
      extraDuplicateEntries,
      soloLineups,
      soloRate,
      singleEntryUsers: users.filter((u) => u.rosters === 1).length,
      maxEntryUsers: users.filter((u) => u.rosters >= 150).length,
      mostDuped,
      fieldExposure,
      users,
      lineups,
      duplicatedLineups,
    };
  }, [rawRows]);

  const tableRows = useMemo(() => {
    const q = lower(globalSearch).trim();

    const globalFilter = (rows, keys) => {
      if (!q) return rows;
      return rows.filter((r) =>
        keys.some((k) => lower(getFilterValue(r, k)).includes(q))
      );
    };

    const fieldBase = globalFilter(contest.fieldExposure, ["driver"]);
    const usersBase = globalFilter(contest.users, ["username", "topDriver"]);

    const dupesBase = contest.duplicatedLineups.filter((r) => {
      if (!q) return true;
      return (
        lower(r.lineup).includes(q) ||
        r.users.some((u) => lower(u).includes(q))
      );
    });

    const lineupsBase = contest.lineups.filter((r) => {
      if (!q) return true;
      return (
        lower(r.lineup).includes(q) ||
        r.users.some((u) => lower(u).includes(q))
      );
    });

    const prep = (tableKey, rows) => {
      const filtered = applyColumnFilters(rows, filters[tableKey] || {});
      return sortRows(filtered, sorts[tableKey] || {});
    };

    return {
      field: prep("field", fieldBase),
      users: prep("users", usersBase),
      dupes: prep("dupes", dupesBase),
      lineups: prep("lineups", lineupsBase),
    };
  }, [contest, globalSearch, filters, sorts]);

  const visibleSelectedUser = useMemo(() => {
    if (activeTab !== "users") return null;

    if (selectedUser) {
      const hit = contest.users.find(
        (u) => lower(u.username) === lower(selectedUser)
      );
      if (hit) return hit;
    }

    if (tableRows.users.length === 1) {
      return tableRows.users[0];
    }

    const q = lower(globalSearch).trim();
    if (q) {
      const exact = contest.users.find((u) => lower(u.username) === q);
      if (exact) return exact;
    }

    return null;
  }, [activeTab, selectedUser, contest.users, tableRows.users, globalSearch]);

  function exportTable(type) {
    if (type === "field") {
      csvDownload(
        "nascar-contest-field-exposure.csv",
        contest.fieldExposure.map((r) => ({
          Driver: r.driver,
          Lineups: r.lineups,
          Exposure: fmtPct(r.exposure, 2),
          DKOfficialExposure:
            r.officialExposure == null ? "" : fmtPct(r.officialExposure, 2),
          Difference:
            r.exposureDiff == null ? "" : formatLeverage(r.exposureDiff),
        }))
      );
    }

    if (type === "users") {
      csvDownload(
        "nascar-contest-users.csv",
        contest.users.map((r) => ({
          Username: r.username,
          Rosters: r.rosters,
          Builds: r.builds,
          SoloEntries: r.fieldUniqueEntries,
          FieldDupedEntries: r.fieldDupedEntries,
          SelfDupedEntries: r.personalDuplicateEntries,
          SoloRate: fmtPct(r.uniqueRate, 2),
          PlayersUsed: r.playersUsed,
          TopDriver: r.topDriver,
          MaxExposure: fmtPct(r.maxExposure, 2),
          AvgPoints: fmtNum(r.avgPoints, 2),
          BestPoints: fmtNum(r.bestPoints, 2),
          BestRank: r.bestRank,
        }))
      );
    }

    if (type === "dupes") {
      csvDownload(
        "nascar-contest-duplicated-lineups.csv",
        contest.duplicatedLineups.map((r) => ({
          Lineup: r.lineup,
          TimesDuped: r.dupes,
          UniqueUsers: r.uniqueUsers,
          Users: r.users.join(", "),
          AvgPoints: fmtNum(r.avgPoints, 2),
          BestPoints: fmtNum(r.bestPoints, 2),
          BestRank: r.bestRank,
        }))
      );
    }

    if (type === "lineups") {
      csvDownload(
        "nascar-contest-all-lineups.csv",
        contest.lineups.map((r) => ({
          Lineup: r.lineup,
          Count: r.dupes,
          UniqueUsers: r.uniqueUsers,
          Users: r.users.join(", "),
          AvgPoints: fmtNum(r.avgPoints, 2),
          BestPoints: fmtNum(r.bestPoints, 2),
          BestRank: r.bestRank,
        }))
      );
    }
  }

  const hasData = rawRows.length > 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-7xl px-4 py-6 space-y-5">
        {/* Header */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 shadow-2xl overflow-hidden">
          <div className="border-b border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-sky-950/40 px-5 py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-xs font-bold tracking-[0.25em] uppercase text-sky-300">
                  DraftKings NASCAR Tool
                </div>
                <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-white">
                  Contest Dashboard
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-400">
                  Upload a DraftKings contest standings CSV or the ZIP directly to analyze field ownership,
                  user exposures, unique lineups, duplicate builds, and user portfolios.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <label className="inline-flex cursor-pointer items-center justify-center rounded-lg border border-sky-500/40 bg-sky-500/15 px-4 py-2 text-sm font-bold text-sky-100 hover:bg-sky-500/25 transition">
                  Upload Contest CSV / ZIP
                  <input
                    type="file"
                    accept=".csv,.zip,text/csv,application/zip,application/x-zip-compressed"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>

                {hasData ? (
                  <button
                    type="button"
                    onClick={() => {
                      setRawRows([]);
                      setFileName("");
                      setParseError("");
                      setGlobalSearch("");
                      setFilters({});
                      setSorts({});
                      setSelectedUser("");
                    }}
                    className="rounded-lg border border-slate-700 bg-slate-950 px-4 py-2 text-sm text-slate-200 hover:bg-slate-900 transition"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="px-5 py-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-xs text-slate-400">
            <div>
              {fileName ? (
                <>
                  Loaded File:{" "}
                  <span className="font-semibold text-slate-100">{fileName}</span>
                </>
              ) : (
                "No contest CSV loaded yet."
              )}
            </div>

            {hasData ? (
              <div className="text-slate-500">
                Parsed{" "}
                <span className="font-bold text-sky-300">
                  {fmtNum(contest.totalLineups)}
                </span>{" "}
                lineups from{" "}
                <span className="font-bold text-sky-300">
                  {fmtNum(contest.totalUsers)}
                </span>{" "}
                users
              </div>
            ) : null}
          </div>
        </div>

        {parseError ? (
          <div className="rounded-xl border border-red-500/30 bg-red-950/30 px-4 py-3 text-sm text-red-200">
            {parseError}
          </div>
        ) : null}

        {!hasData ? (
          <EmptyState />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard title="Contest Lineups" value={fmtNum(contest.totalLineups)} />
              <StatCard title="Contest Users" value={fmtNum(contest.totalUsers)} />
              <StatCard title="Unique Lineups" value={fmtNum(contest.uniqueLineups)} />
              <StatCard title="Dupe Combos" value={fmtNum(contest.duplicatedCombos)} />
              <StatCard title="Entries In Dupes" value={fmtNum(contest.entriesInDuplicatedLineups)} />
              <StatCard title="Extra Dupe Copies" value={fmtNum(contest.extraDuplicateEntries)} />
              <StatCard title="Solo Lineups" value={fmtNum(contest.soloLineups)} />
              <StatCard title="Solo Rate" value={fmtPct(contest.soloRate, 1)} />
              <StatCard title="150-Max Users" value={fmtNum(contest.maxEntryUsers)} />
              <StatCard title="Single-Entry Users" value={fmtNum(contest.singleEntryUsers)} />
              <StatCard
                title="Most Duped"
                value={contest.mostDuped ? `${contest.mostDuped.dupes}x` : "0x"}
              />
            </div>

            {contest.mostDuped ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 overflow-hidden">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-slate-800 px-5 py-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-sky-300">
                      Most Duplicated Build
                    </div>
                    <h2 className="mt-1 text-xl font-extrabold text-white">
                      {contest.mostDuped.dupes} Entries • {contest.mostDuped.uniqueUsers} Users
                    </h2>
                    <p className="mt-1 text-sm text-slate-400">
                      Avg Points:{" "}
                      <span className="text-slate-100 font-semibold">
                        {fmtNum(contest.mostDuped.avgPoints, 2)}
                      </span>{" "}
                      | Best Rank:{" "}
                      <span className="text-slate-100 font-semibold">
                        {contest.mostDuped.bestRank || "-"}
                      </span>
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveTab("dupes")}
                    className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-2 text-sm font-bold text-sky-200 hover:bg-sky-500/20"
                  >
                    View Duplicates
                  </button>
                </div>

                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {contest.mostDuped.players.map((p) => (
                    <div
                      key={p}
                      className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm font-semibold text-slate-100"
                    >
                      {p}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  <TabButton label="Overview" value="overview" activeTab={activeTab} setActiveTab={setActiveTab} />
                  <TabButton label="Field Exposure" value="field" activeTab={activeTab} setActiveTab={setActiveTab} />
                  <TabButton label="Users" value="users" activeTab={activeTab} setActiveTab={setActiveTab} />
                  <TabButton label="Duplicates" value="dupes" activeTab={activeTab} setActiveTab={setActiveTab} />
                  <TabButton label="All Lineups" value="lineups" activeTab={activeTab} setActiveTab={setActiveTab} />
                </div>

                <input
                  value={globalSearch}
                  onChange={(e) => {
                    setGlobalSearch(e.target.value);
                    if (activeTab === "users") setSelectedUser("");
                  }}
                  placeholder="Search driver, user, or lineup..."
                  className="w-full lg:w-96 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>
            </div>

            {activeTab === "overview" ? <OverviewTab contest={contest} /> : null}

            {activeTab === "field" ? (
              <Panel
                title="Field Exposure"
                subtitle="Lineup exposure calculated from the actual uploaded contest lineups."
                action={<ExportButton onClick={() => exportTable("field")} />}
              >
                <FilterableTable
                  tableKey="field"
                  columns={[
                    { key: "driver", label: "Driver" },
                    { key: "lineups", label: "Lineups", align: "right", format: (v) => fmtNum(v) },
                    { key: "exposure", label: "Calc Exp", align: "right", format: (v) => fmtPct(v, 2) },
                    {
                      key: "officialExposure",
                      label: "DK %Drafted",
                      align: "right",
                      format: (v) => (v == null ? "-" : fmtPct(v, 2)),
                    },
                    {
                      key: "exposureDiff",
                      label: "Diff",
                      align: "right",
                      format: (v) => (v == null ? "-" : formatLeverage(v)),
                    },
                  ]}
                  rows={tableRows.field}
                  sort={sorts.field || {}}
                  filters={filters.field || {}}
                  onSort={setTableSort}
                  onFilter={setTableFilter}
                  onClearFilters={clearFilters}
                />
              </Panel>
            ) : null}

            {activeTab === "users" ? (
              <>
                {visibleSelectedUser ? (
                  <UserPortfolio
                    user={visibleSelectedUser}
                    onClose={() => {
                      setSelectedUser("");
                      setGlobalSearch("");
                      clearFilters("users");
                    }}
                  />
                ) : null}

                <Panel
                  title="Contest Users"
                  subtitle="Click a user to open their portfolio. Builds are that user's personal unique builds; Solo means unique across the full contest."
                  action={<ExportButton onClick={() => exportTable("users")} />}
                >
                  <FilterableTable
                    tableKey="users"
                    columns={[
                      {
                        key: "username",
                        label: "Username",
                        render: (row) => (
                          <button
                            type="button"
                            onClick={() => setSelectedUser(row.username)}
                            className="font-semibold text-sky-200 hover:text-sky-100 hover:underline"
                          >
                            {row.username}
                          </button>
                        ),
                      },
                      { key: "rosters", label: "Rosters", align: "right", format: (v) => fmtNum(v) },
                      { key: "builds", label: "Builds", align: "right", format: (v) => fmtNum(v) },
                      { key: "fieldUniqueEntries", label: "Solo", align: "right", format: (v) => fmtNum(v) },
                      { key: "fieldDupedEntries", label: "Field Dupes", align: "right", format: (v) => fmtNum(v) },
                      { key: "personalDuplicateEntries", label: "Self Dupes", align: "right", format: (v) => fmtNum(v) },
                      { key: "uniqueRate", label: "Solo %", align: "right", format: (v) => fmtPct(v, 1) },
                      { key: "playersUsed", label: "Drivers Used", align: "right", format: (v) => fmtNum(v) },
                      { key: "topDriver", label: "Top Driver" },
                      { key: "maxExposure", label: "Max Exp", align: "right", format: (v) => fmtPct(v, 1) },
                      { key: "avgPoints", label: "Avg Pts", align: "right", format: (v) => fmtNum(v, 2) },
                      { key: "bestPoints", label: "Best Pts", align: "right", format: (v) => fmtNum(v, 2) },
                      { key: "bestRank", label: "Best Rank", align: "right" },
                    ]}
                    rows={tableRows.users}
                    sort={sorts.users || {}}
                    filters={filters.users || {}}
                    onSort={setTableSort}
                    onFilter={setTableFilter}
                    onClearFilters={clearFilters}
                  />
                </Panel>
              </>
            ) : null}

            {activeTab === "dupes" ? (
              <Panel
                title="Duplicated Lineups"
                subtitle="Only lineups appearing more than once in the contest."
                action={<ExportButton onClick={() => exportTable("dupes")} />}
              >
                <FilterableTable
                  tableKey="dupes"
                  columns={[
                    { key: "dupes", label: "Dupes", align: "right", format: (v) => fmtNum(v) },
                    { key: "uniqueUsers", label: "Users", align: "right", format: (v) => fmtNum(v) },
                    { key: "lineup", label: "Lineup", wide: true, render: (row) => <LineupChips players={row.players} /> },
                    { key: "avgPoints", label: "Avg Pts", align: "right", format: (v) => fmtNum(v, 2) },
                    { key: "bestPoints", label: "Best Pts", align: "right", format: (v) => fmtNum(v, 2) },
                    { key: "bestRank", label: "Best Rank", align: "right" },
                    {
                      key: "users",
                      label: "Usernames",
                      wide: true,
                      render: (row) =>
                        row.users.slice(0, 12).join(", ") +
                        (row.users.length > 12
                          ? ` +${row.users.length - 12} more`
                          : ""),
                    },
                  ]}
                  rows={tableRows.dupes}
                  sort={sorts.dupes || {}}
                  filters={filters.dupes || {}}
                  onSort={setTableSort}
                  onFilter={setTableFilter}
                  onClearFilters={clearFilters}
                />
              </Panel>
            ) : null}

            {activeTab === "lineups" ? (
              <Panel
                title="All Lineups"
                subtitle="Every unique lineup build in the contest, including singletons and duplicated builds."
                action={<ExportButton onClick={() => exportTable("lineups")} />}
              >
                <FilterableTable
                  tableKey="lineups"
                  columns={[
                    { key: "dupes", label: "Count", align: "right", format: (v) => fmtNum(v) },
                    { key: "uniqueUsers", label: "Users", align: "right", format: (v) => fmtNum(v) },
                    { key: "lineup", label: "Lineup", wide: true, render: (row) => <LineupChips players={row.players} /> },
                    { key: "avgPoints", label: "Avg Pts", align: "right", format: (v) => fmtNum(v, 2) },
                    { key: "bestPoints", label: "Best Pts", align: "right", format: (v) => fmtNum(v, 2) },
                    { key: "bestRank", label: "Best Rank", align: "right" },
                    {
                      key: "users",
                      label: "Usernames",
                      wide: true,
                      render: (row) =>
                        row.users.slice(0, 12).join(", ") +
                        (row.users.length > 12
                          ? ` +${row.users.length - 12} more`
                          : ""),
                    },
                  ]}
                  rows={tableRows.lineups}
                  sort={sorts.lineups || {}}
                  filters={filters.lineups || {}}
                  onSort={setTableSort}
                  onFilter={setTableFilter}
                  onClearFilters={clearFilters}
                />
              </Panel>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
   UI Pieces
============================================================================ */

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/50 p-10 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-sky-500/30 bg-sky-500/10 text-2xl">
        📊
      </div>
      <h2 className="text-xl font-extrabold text-white">
        Upload a DraftKings contest standings CSV or ZIP
      </h2>
      <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-400">
        You can upload the DraftKings download ZIP without extracting it first. The CSV should include
        EntryName, EntryId, Rank, Points, and Lineup. Once loaded, this page will calculate contest ownership,
        user exposures, lineup duplication, and unique builds.
      </p>
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-4 shadow-lg">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
        {title}
      </div>
      <div className="mt-2 text-2xl font-extrabold text-white">{value}</div>
    </div>
  );
}

function TabButton({ label, value, activeTab, setActiveTab }) {
  const active = activeTab === value;

  return (
    <button
      type="button"
      onClick={() => setActiveTab(value)}
      className={[
        "rounded-lg px-3 py-2 text-sm font-bold transition border",
        active
          ? "border-sky-500/50 bg-sky-500/20 text-sky-100"
          : "border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-900 hover:text-sky-200",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function Panel({ title, subtitle, children, action }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 overflow-hidden shadow-2xl">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-slate-800 px-5 py-4">
        <div>
          <h2 className="text-lg font-extrabold text-white">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-slate-400">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ExportButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-900 hover:text-sky-200 transition"
    >
      Export CSV
    </button>
  );
}

function OverviewTab({ contest }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Panel title="Top Field Exposure" subtitle="Highest-owned drivers in the uploaded contest.">
        <MiniTable
          columns={["Driver", "Lineups", "Calc Exp", "DK %Drafted", "Diff"]}
          rows={contest.fieldExposure.slice(0, 15).map((r) => [
            r.driver,
            fmtNum(r.lineups),
            fmtPct(r.exposure, 1),
            r.officialExposure == null ? "-" : fmtPct(r.officialExposure, 1),
            r.exposureDiff == null ? "-" : formatLeverage(r.exposureDiff),
          ])}
        />
      </Panel>

      <Panel title="Top Users By Entries" subtitle="Users with the most lineups in the contest.">
        <MiniTable
          columns={["User", "Rosters", "Builds", "Solo", "Solo %", "Field Dupes", "Top Driver"]}
          rows={contest.users.slice(0, 15).map((r) => [
            r.username,
            fmtNum(r.rosters),
            fmtNum(r.builds),
            fmtNum(r.fieldUniqueEntries),
            fmtPct(r.uniqueRate, 1),
            fmtNum(r.fieldDupedEntries),
            r.topDriver,
          ])}
        />
      </Panel>

      <Panel title="Most Duplicated Lineups" subtitle="Lineup combinations that appear the most often.">
        <MiniTable
          columns={["Dupes", "Users", "Avg Pts", "Best Rank", "Lineup"]}
          rows={contest.duplicatedLineups.slice(0, 10).map((r) => [
            fmtNum(r.dupes),
            fmtNum(r.uniqueUsers),
            fmtNum(r.avgPoints, 2),
            r.bestRank || "-",
            r.players.join(" / "),
          ])}
        />
      </Panel>

      <Panel title="Highest Max User Exposures" subtitle="Users most concentrated on a single driver.">
        <MiniTable
          columns={["User", "Top Driver", "Max Exp", "Rosters", "Builds"]}
          rows={[...contest.users]
            .sort((a, b) => b.maxExposure - a.maxExposure)
            .slice(0, 10)
            .map((r) => [
              r.username,
              r.topDriver,
              fmtPct(r.maxExposure, 1),
              fmtNum(r.rosters),
              fmtNum(r.builds),
            ])}
        />
      </Panel>
    </div>
  );
}

function UserPortfolio({ user, onClose }) {
  return (
    <div className="rounded-2xl border border-sky-500/30 bg-slate-900/80 overflow-hidden shadow-2xl">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-slate-800 px-5 py-4 bg-sky-950/20">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-sky-300">
            User Portfolio
          </div>
          <h2 className="mt-1 text-2xl font-extrabold text-white">
            {user.username}
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            {fmtNum(user.rosters)} rosters • {fmtNum(user.builds)} builds •{" "}
            {fmtNum(user.fieldUniqueEntries)} solo •{" "}
            {fmtNum(user.fieldDupedEntries)} field-duped
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-900 hover:text-sky-200"
        >
          Close Portfolio
        </button>
      </div>

      <div className="p-4 space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatCard title="Rosters" value={fmtNum(user.rosters)} />
          <StatCard title="Builds" value={fmtNum(user.builds)} />
          <StatCard title="Solo" value={fmtNum(user.fieldUniqueEntries)} />
          <StatCard title="Field Dupes" value={fmtNum(user.fieldDupedEntries)} />
          <StatCard title="Best Rank" value={user.bestRank || "-"} />
          <StatCard title="Best Pts" value={fmtNum(user.bestPoints, 2)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel
            title="Driver Exposure"
            subtitle="How often this user used each driver compared to the field."
          >
            <MiniTable
              columns={["Driver", "Lineups", "User Exp", "Field Exp", "+/-"]}
              rows={user.driverExposures.slice(0, 30).map((r) => [
                r.driver,
                fmtNum(r.lineups),
                fmtPct(r.exposure, 1),
                fmtPct(r.fieldExposure, 1),
                formatLeverage(r.leverage),
              ])}
            />
          </Panel>

          <Panel
            title="Lineup Portfolio"
            subtitle="Each lineup entered by this user, sorted by rank."
          >
            <div className="overflow-auto max-h-[520px] rounded-xl border border-slate-800">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-950">
                  <tr className="border-b border-slate-800 text-slate-300">
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">
                      Pts
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">
                      Field Dupes
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">
                      Lineup
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {user.lineupRows.map((row, idx) => (
                    <tr
                      key={`${row.entryId}-${idx}`}
                      className="border-b border-slate-800/80 hover:bg-slate-800/40"
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-slate-200">
                        {row.rank || "-"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-slate-200">
                        {fmtNum(row.points, 2)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span
                          className={[
                            "inline-flex rounded-md px-2 py-1 text-xs font-bold",
                            row.isSolo
                              ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                              : "bg-yellow-500/10 text-yellow-300 border border-yellow-500/30",
                          ].join(" ")}
                        >
                          {row.isSolo ? "Solo" : `${row.fieldDupeCount}x`}
                        </span>
                      </td>
                      <td className="px-3 py-2 min-w-[420px]">
                        <LineupChips players={row.players} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function MiniTable({ columns, rows }) {
  return (
    <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-900 z-10">
          <tr className="border-b border-slate-800 text-slate-300">
            {columns.map((c) => (
              <th
                key={c}
                className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className="border-b border-slate-800/80 hover:bg-slate-800/40"
            >
              {r.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-slate-200 whitespace-nowrap">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LineupChips({ players }) {
  return (
    <div className="flex min-w-[520px] flex-wrap gap-1.5">
      {(players || []).map((p) => (
        <span
          key={p}
          className="inline-flex items-center rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] font-semibold text-slate-200"
        >
          {p}
        </span>
      ))}
    </div>
  );
}

function FilterableTable({
  tableKey,
  columns,
  rows,
  sort,
  filters,
  onSort,
  onFilter,
  onClearFilters,
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-400">
          Showing{" "}
          <span className="font-bold text-sky-300">{fmtNum(rows.length)}</span>{" "}
          rows
        </div>

        <button
          type="button"
          onClick={() => onClearFilters(tableKey)}
          className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-900 hover:text-sky-200"
        >
          Clear Header Filters
        </button>
      </div>

      <div className="overflow-auto max-h-[680px] rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20 bg-slate-950">
            <tr className="border-b border-slate-800">
              {columns.map((col) => {
                const activeSort = sort?.key === col.key;
                const arrow = activeSort ? (sort.dir === "asc" ? "▲" : "▼") : "↕";

                return (
                  <th
                    key={col.key}
                    className={[
                      "px-3 py-2 align-bottom bg-slate-950",
                      col.align === "right" ? "text-right" : "text-left",
                      col.wide ? "min-w-[420px]" : "whitespace-nowrap",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      onClick={() => onSort(tableKey, col.key)}
                      className={[
                        "mb-2 inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-wider hover:text-sky-300",
                        activeSort ? "text-sky-300" : "text-slate-300",
                        col.align === "right" ? "justify-end" : "justify-start",
                      ].join(" ")}
                    >
                      <span>{col.label}</span>
                      <span className="text-[10px]">{arrow}</span>
                    </button>

                    <input
                      value={filters?.[col.key] || ""}
                      onChange={(e) => onFilter(tableKey, col.key, e.target.value)}
                      placeholder="Filter..."
                      className={[
                        "block w-full rounded-md border border-slate-800 bg-slate-900/80 px-2 py-1.5 text-xs font-normal text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500",
                        col.align === "right" ? "text-right" : "text-left",
                      ].join(" ")}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rows.length ? (
              rows.map((row, idx) => (
                <tr
                  key={row.lineupKey || row.username || row.driver || idx}
                  className="border-b border-slate-800/80 hover:bg-slate-800/40"
                >
                  {columns.map((col) => {
                    const raw = row?.[col.key];
                    const content = col.render
                      ? col.render(row)
                      : col.format
                      ? col.format(raw, row)
                      : Array.isArray(raw)
                      ? raw.join(", ")
                      : raw;

                    return (
                      <td
                        key={col.key}
                        className={[
                          "px-3 py-2 text-slate-200 align-top",
                          col.align === "right" ? "text-right" : "text-left",
                          col.wide ? "min-w-[420px]" : "whitespace-nowrap",
                        ].join(" ")}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-sm text-slate-500"
                >
                  No rows match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}